import {
  Box,
  Button,
  HStack,
  Tag,
  TagLabel,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { ApiMessageStack, Error, Loader } from "components";
import {
  DEFAULT_ERROR_TOAST_OPTIONS,
  DEFAULT_SUCCESS_TOAST_OPTIONS,
} from "constants/default-toast-options";
import { AccountIntegrationTypeEnum } from "enums/account-integration-type.enum";
import { ApiRequestStatusEnum } from "enums/api-request-status.enum";
import { observer } from "mobx-react";
import { FC, useCallback, useEffect, useState } from "react";
import { UserAccountStore } from "store/UserAccounts/UserAccount/UserAccount.store";
import { OfficeSelectIntegrationTable } from "../AccountIntegrationConfigurator/OfficeSelectIntegrationTable";

interface IProps {
  accountStore: UserAccountStore;
}

export const JupixIntegrationConfigurator: FC<IProps> = observer(
  ({ accountStore }) => {
    const toast = useToast();
    const [isConnecting, setIsConnecting] = useState(false);
    const [loadingStatus, setLoadingStatus] = useState(
      ApiRequestStatusEnum.NONE
    );

    const accountOfficesStore = accountStore.accountOfficesStore;
    const accountIntegrationsStore = accountStore.accountIntegrationsStore;
    const accountIntegrationOfficesStore =
      accountIntegrationsStore.accountIntegrationJupixStore
        .accountIntegrationJupixOfficesStore;

    const isConnected = accountIntegrationsStore.hasConnectedJupixIntegration;

    const loadOfficeData = useCallback(async () => {
      try {
        setLoadingStatus(ApiRequestStatusEnum.PENDING);
        await Promise.all([
          accountOfficesStore.fetchAccountOffices(),
          accountIntegrationOfficesStore.fetchAccountIntegrationOfficesAndRelations(),
        ]);
        setLoadingStatus(ApiRequestStatusEnum.SUCCESS);
      } catch {
        setLoadingStatus(ApiRequestStatusEnum.ERROR);
      }
    }, [accountOfficesStore, accountIntegrationOfficesStore]);

    useEffect(() => {
      loadOfficeData();
    }, [loadOfficeData]);

    const onConnect = useCallback(async () => {
      try {
        setIsConnecting(true);
        await accountIntegrationsStore.connectJupix();
        toast({
          ...DEFAULT_SUCCESS_TOAST_OPTIONS,
          description: <ApiMessageStack messageStack={"Jupix connected."} />,
        });
      } catch (e) {
        toast({
          ...DEFAULT_ERROR_TOAST_OPTIONS,
          description: <ApiMessageStack messageStack={e?.message} />,
        });
        return;
      } finally {
        setIsConnecting(false);
      }

      await loadOfficeData();
    }, [accountIntegrationsStore, loadOfficeData, toast]);

    return (
      <VStack align={"stretch"} spacing={5}>
        <HStack spacing={3}>
          <Text fontWeight={"medium"}>Connection status</Text>
          <Tag
            size={"sm"}
            variant={"subtle"}
            colorScheme={isConnected ? "green" : "red"}
          >
            <TagLabel>{isConnected ? "Connected" : "Not connected"}</TagLabel>
          </Tag>
        </HStack>

        <Text fontSize={"sm"} color={"gray.500"}>
          {isConnected
            ? "Jupix is authorised. Use Reconnect if you need to refresh the authorisation."
            : "Your credentials are saved. Click Connect to authorise LeadPro with Jupix, then map your offices below."}
        </Text>

        <Button
          alignSelf={"flex-start"}
          colorScheme={"blue"}
          onClick={onConnect}
          isLoading={isConnecting}
          isDisabled={isConnecting}
        >
          {isConnected ? "Reconnect" : "Connect"}
        </Button>

        <Box position={"relative"} minHeight={"200px"}>
          {loadingStatus === ApiRequestStatusEnum.PENDING && <Loader />}
          {loadingStatus === ApiRequestStatusEnum.ERROR && (
            <Error
              message={`Could not load your Jupix offices. Try again, or use ${
                isConnected ? "Reconnect" : "Connect"
              } above to re-authorise.`}
              action={
                <Button colorScheme={"blue"} onClick={loadOfficeData}>
                  Try again
                </Button>
              }
            />
          )}
          {loadingStatus === ApiRequestStatusEnum.SUCCESS && (
            <OfficeSelectIntegrationTable
              accountIntegrationOfficesStore={accountIntegrationOfficesStore}
              accountIntegrationsStore={accountIntegrationsStore}
              integrationType={AccountIntegrationTypeEnum.JUPIX}
            />
          )}
        </Box>
      </VStack>
    );
  }
);
